import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Share2, Bookmark, Home } from 'lucide-react';
import SEO from '@/components/SEO';
import { generateArticleStructuredData } from '@/components/SEO';

// Simple markdown-like rendering
const renderContent = (content) => {
  const lines = content.trim().split('\n');
  const elements = [];
  let currentList = [];
  let listType = null;

  const flushList = () => {
    if (currentList.length > 0) {
      if (listType === 'ul') {
        elements.push(<ul key={elements.length} className="list-disc pl-6 mb-4 space-y-2">{currentList}</ul>);
      } else {
        elements.push(<ol key={elements.length} className="list-decimal pl-6 mb-4 space-y-2">{currentList}</ol>);
      }
      currentList = [];
      listType = null;
    }
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    if (trimmed.startsWith('# ')) {
      flushList();
      elements.push(<h1 key={index} className="text-3xl font-bold mb-4 mt-6">{trimmed.slice(2)}</h1>);
    } else if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(<h2 key={index} className="text-2xl font-bold mb-3 mt-6">{trimmed.slice(3)}</h2>);
    } else if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(<h3 key={index} className="text-xl font-semibold mb-2 mt-4">{trimmed.slice(4)}</h3>);
    } else if (trimmed.startsWith('- ')) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      currentList.push(<li key={index} className="text-muted-foreground">{trimmed.slice(2)}</li>);
    } else if (/^\d+\.\s/.test(trimmed)) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      currentList.push(<li key={index} className="text-muted-foreground">{trimmed.replace(/^\d+\.\s/, '')}</li>);
    } else if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      flushList();
      elements.push(<p key={index} className="font-bold mb-2">{trimmed.replace(/\*\*/g, '')}</p>);
    } else if (trimmed) {
      flushList();
      elements.push(<p key={index} className="text-muted-foreground mb-4 leading-relaxed">{trimmed}</p>);
    }
  });

  flushList();
  return elements;
};

const BlogPost = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch('/api/blog-posts');
        if (!response.ok) throw new Error('Failed to fetch posts');
        const data = await response.json();
        const posts = (data.posts || []).map(post => ({
          id: post.id,
          title: post.title,
          heading: post.heading_override || post.title,
          excerpt: post.excerpt,
          content: post.content,
          author: post.author,
          date: post.published_at,
          image: post.image_url || '/idnty-logo.svg',
          category: post.tags?.[0] || 'Company Update',
          readTime: '3 min read',
          linkedin_url: post.linkedin_url
        }));
        setAllArticles(posts);
        const foundArticle = posts.find(a => a.id === id);
        setArticle(foundArticle || null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">Loading article...</div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">
            {error || "The article you're looking for doesn't exist."}
          </p>
          <Link to="/blog" className="text-primary hover:underline">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

   const displayTitle = article.heading || article.title;

   return (
     <div className="min-h-screen bg-background">
       <SEO
         title={displayTitle}
         description={article.excerpt}
         keywords={`${article.category.toLowerCase()}, identity management, cybersecurity, ${displayTitle.toLowerCase()}`}
         ogTitle={displayTitle}
         ogDescription={article.excerpt}
         ogImage={`https://idnty.one${article.image}`}
         ogType="article"
         twitterTitle={displayTitle}
         twitterDescription={article.excerpt}
         twitterImage={`https://idnty.one${article.image}`}
         canonicalUrl={`https://idnty.one/blog/${article.id}`}
         structuredData={generateArticleStructuredData({
           headline: displayTitle,
           description: article.excerpt,
           image: `https://idnty.one${article.image}`,
           author: article.author,
           datePublished: article.date,
           url: `https://idnty.one/blog/${article.id}`
         })}
       />
      {/* Header with Home Link */}
      <header className="bg-white border-b border-border py-4">
        <div className="container mx-auto px-4">
          <Link 
            to="https://www.idnty.one" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <Home className="w-4 h-4 mr-2" />
            Back to IDNTY
          </Link>
        </div>
      </header>

      {/* Header */}
      <header className="bg-gradient-to-b from-primary/5 to-background py-12">
        <div className="container mx-auto px-4">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-muted-foreground hover:text-primary mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>

          {/* Article Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
            <span className="bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
              {article.category}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(article.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {article.readTime}
            </span>
          </div>

           {/* Title */}
           <h1 className="text-4xl font-bold mb-6 tracking-tight">
             {article.heading || article.title}
           </h1>

          {/* Author */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium">{article.author}</p>
              <p className="text-sm text-muted-foreground">IDNTY Security Team</p>
            </div>
          </div>
        </div>
      </header>

       {/* Featured Image */}
       <section className="container mx-auto px-4 -mt-4">
         <div className="max-w-4xl mx-auto">
           <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-6 sm:p-12 flex items-center justify-center">
             <img 
               src={article.image || '/idnty-logo.svg'} 
               alt={article.title}
               className="h-16 sm:h-32 w-auto object-contain"
             />
           </div>
         </div>
       </section>

      {/* Article Content */}
      <article className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Share & Save */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 pb-8 border-b border-border">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
                <button className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                  <Bookmark className="w-4 h-4" />
                  Save
                </button>
              </div>
              {article.linkedin_url && (
                <a
                  href={article.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 text-sm text-muted-foreground bg-secondary rounded-lg hover:bg-secondary/80 transition-colors w-full sm:w-auto justify-center"
                >
                  View on LinkedIn
                </a>
              )}
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {renderContent(article.content)}
            </div>

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-border">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-muted-foreground">Tags:</span>
                {article.category && (
                  <span className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm">
                    {article.category}
                  </span>
                )}
                {['Identity Management', 'Access Control', 'Security'].map((tag) => (
                  <span 
                    key={tag}
                    className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8">Related Articles</h2>
          {allArticles.filter(a => a.id !== article.id).length > 0 ? (
            <div className="grid gap-6 md:grid-cols-3">
              {allArticles
                .filter(a => a.id !== article.id)
                .slice(0, 3)
                .map((relatedArticle) => (
                  <Link 
                    key={relatedArticle.id}
                    to={`/blog/${relatedArticle.id}`}
                    className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-shadow"
                  >
                    <span className="text-xs text-primary font-medium mb-2 block">
                      {relatedArticle.category}
                    </span>
                    <h3 className="font-semibold mb-2 line-clamp-2">
                      {relatedArticle.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {relatedArticle.excerpt}
                    </p>
                  </Link>
                ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No related articles yet.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
